import React, { useEffect, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import { Provider } from 'react-redux';
import { BrowserRouter as Router } from 'react-router-dom';
import { injectStyle } from 'react-toastify/dist/inject-style';

import { Box } from '@mui/material';
import {
  StyledEngineProvider,
  ThemeProvider,
  createTheme,
} from '@mui/material/styles';

import { Capacitor } from '@capacitor/core';
import {
  MouseTransition,
  MultiBackend,
  type MultiBackendOptions,
  TouchTransition,
} from 'dnd-multi-backend';

import AppRoutes from './AppRoutes';
import Loader from './components/layout/loader/Loader';
import ToastRoot from './components/layout/toast-root/ToastRoot';
import ErrorBoundary from './components/reusable/error-boundary/ErrorBoundary';
import { BackLayerStackProvider } from './context/BackLayerStackContext';
import combineProviders from './context/CombinedContextProvider';
import { NavigationStackProvider } from './context/NavigationStackContext';
import { useVisualViewportCssVars } from './hooks/use-visual-viewport-css-vars';
import './languages/i18n';
import OnboardingProvider from './onboarding/OnboardingProvider';
import { store } from './redux/store';
import './shared/firebase/firebase-client';
import { waitForAuthInit } from './shared/firebase/firebase-client';

const dndProviderOptions: MultiBackendOptions = {
  backends: [
    {
      id: 'html5',
      backend: HTML5Backend,
      transition: MouseTransition,
    },
    {
      id: 'touch',
      backend: TouchBackend,
      preview: true,
      transition: TouchTransition,
      options: {
        delay: 0,
        delayTouchStart: 0,
        delayMouseStart: 0,
        enableMouseEvents: false,
      },
    },
  ],
};

const AppContextProvider = combineProviders();

const bootstrapTheme = createTheme();

function App() {
  const isNative = Capacitor.isNativePlatform();
  const [firebaseAuthInited, setFirebaseAuthInited] = useState(isNative);
  useVisualViewportCssVars();

  useEffect(() => {
    injectStyle();
    if (isNative) {
      return;
    }
    waitForAuthInit().then(() => {
      setFirebaseAuthInited(true);
    });
  }, [isNative]);

  if (!firebaseAuthInited) {
    return (
      <ThemeProvider theme={bootstrapTheme}>
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            minHeight: '100dvh',
          }}>
          <Loader />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <DndProvider
      backend={MultiBackend}
      context={window}
      options={dndProviderOptions}>
      <Provider store={store}>
        <ErrorBoundary>
          <StyledEngineProvider injectFirst>
            <AppContextProvider>
              <Router>
                <NavigationStackProvider>
                  <BackLayerStackProvider>
                    <OnboardingProvider>
                      <AppRoutes />
                    </OnboardingProvider>
                  </BackLayerStackProvider>
                </NavigationStackProvider>
              </Router>
              <ToastRoot />
            </AppContextProvider>
          </StyledEngineProvider>
        </ErrorBoundary>
      </Provider>
    </DndProvider>
  );
}

export default App;
